import axios from 'axios';
import dayjs from 'dayjs';
const MEDIA_CACHE_KEY = 'signage-media-cache';
const JSON_CACHE_KEY = 'signage-json-cache';
const failedMediaUrls = new Set();
const memoryBlobCache = new Map();
const TRANSPARENT_PIXEL = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=';

export default {
    install: (app) => {
        // Brand
        app.config.globalProperties.$brand = function (text) {
            if (!text) return '';
            const brandName = process.env.VUE_APP_BRAND_NAME || 'Nonius';
            
            // Replace "Nonius" (case-insensitive) with the environment brand name
            return text.toString().replace(/nonius/gi, brandName);
        };

        // Truncate text
        app.config.globalProperties.$truncateText = function (text, length, clamp = '...') {
            const content = text.trim();
            return content.length > length ? `${content.slice(0, length)}${clamp}` : content;
        };

        // Lazy Caller
        app.config.globalProperties.$lazyCaller = function (propertyName, value, time = 500) {
            clearTimeout(this._lazyCallerTimeout);
            this._lazyCallerTimeout = setTimeout(() => {
                this[propertyName] = value;
            }, time);
        };

        // URL Protocol fix
        app.config.globalProperties.$replaceHttpToHttps = function (url) {
            if (process.env.VUE_APP_TITLE !== 'Nonius Signage Staging') return url;
            return url.replace('http://', 'https://');
        };

        // Backend URL prepend
        app.config.globalProperties.$addBackendUrl = function (url) {
            if (url.startsWith(process.env.VUE_APP_BACKEND_URL)) return url;
            return `${process.env.VUE_APP_BACKEND_URL}${url}`;
        };

        app.config.globalProperties.$getFromNowUTC = function(date) {
            return dayjs(dayjs(date).format('YYYY-MM-DD HH:mm')).from(dayjs().utc().format('YYYY-MM-DD HH:mm'));
        };

        const activeFetches = new Map(); 

        app.config.globalProperties.$getCachedMedia = async function (url) {
            if (!url) return '';
            const fullUrl = url;
            const fileName = fullUrl.split('/').pop() || fullUrl;

            if (failedMediaUrls.has(fullUrl)) {
                return TRANSPARENT_PIXEL;
            }

            if (memoryBlobCache.has(fullUrl)) {
                return memoryBlobCache.get(fullUrl);
            }

            // If a fetch is already in progress for this URL, wait for it to finish
            if (activeFetches.has(fullUrl)) {
                return activeFetches.get(fullUrl);
            }

            // Wrapped the fetching logic in a promise so we can track
            const fetchPromise = (async () => {
                try {
                    const cache = await caches.open(MEDIA_CACHE_KEY);
                    const cachedResponse = await cache.match(fullUrl);

                    if (cachedResponse) {
                        const blob = await cachedResponse.blob();
                        const blobUrl = URL.createObjectURL(blob);
                        memoryBlobCache.set(fullUrl, blobUrl);
                        return blobUrl;
                    }

                    const response = await fetch(fullUrl);
                    
                    // Only cache valid responses
                    if (!response.ok) throw new Error(`Network failed with status: ${response.status}`);

                    await cache.put(fullUrl, response.clone());
                    const blob = await response.blob();
                    const blobUrl = URL.createObjectURL(blob);
                    memoryBlobCache.set(fullUrl, blobUrl);
                    return blobUrl;
                    
                } catch (e) {
                    console.warn(`[Cache] ❌ ERROR loading ${fileName}:`, e.message);
                    // Optional: Only blacklist on 404s, not transient fetch errors
                    if (e.message.includes("404")) {
                        failedMediaUrls.add(fullUrl);
                    }
                    return TRANSPARENT_PIXEL;
                } finally {
                    // Clean up the active fetch tracker regardless of success/fail
                    activeFetches.delete(fullUrl); 
                }
            })();

            // Store the active promise
            activeFetches.set(fullUrl, fetchPromise);
            return fetchPromise;
        };


        // Unified JSON Cache Logic
        app.config.globalProperties.$fetchJsonWithCache = async function (url, strategy = 'network-first', method = 'GET') {
            if (!url) return null;
            const fileName = url.split('/').pop() || url;
            const cache = await caches.open(JSON_CACHE_KEY);

            if (strategy === 'cache-first') {
                const cachedResponse = await cache.match(url);
                if (cachedResponse) {
                    console.log(`%c[JSON] 💾 DISK HIT: ${fileName}`, "color: #4CAF50; font-weight: bold;");
                    return await cachedResponse.json();
                }

                console.log(`%c[JSON] 🌐 MISS (Fetching): ${fileName}`, "color: #FF9800; font-weight: bold;");
                try {
                    // Check if we should POST or GET
                    const response = method === 'POST' ? await axios.post(url) : await axios.get(url);
                    const responseData = response.data;

                    const cacheResponse = new Response(JSON.stringify(responseData), {
                        headers: { 'Content-Type': 'application/json' }
                    });
                    await cache.put(url, cacheResponse);
                    console.log(`%c[JSON] 📦 STORED: ${fileName}`, "color: #2196F3; font-weight: bold;");

                    return responseData;
                } catch (error) {
                    console.error(`%c[JSON] ❌ ERROR fetching ${fileName}:`, "color: #E91E63; font-weight: bold;", error);
                    throw error;
                }
            }

            // --- STRATEGY 2: NETWORK FIRST ---
            try {
                console.log(`%c[JSON] 🌐 Fetching fresh data: ${fileName}`, "color: #FF9800; font-weight: bold;");

                // Check if we should POST or GET
                const response = method === 'POST' ? await axios.post(url) : await axios.get(url);
                const responseData = response.data;

                const cacheResponse = new Response(JSON.stringify(responseData), {
                    headers: { 'Content-Type': 'application/json' }
                });
                await cache.put(url, cacheResponse);
                console.log(`%c[JSON] 📦 STORED: ${fileName}`, "color: #2196F3; font-weight: bold;");

                return responseData;

            } catch (networkError) {
                console.warn(`%c[JSON] ⚠️ Network failed for ${fileName}. Checking offline cache...`, "color: #f44336; font-weight: bold;");

                const cachedResponse = await cache.match(url);
                if (cachedResponse) {
                    console.log(`%c[JSON] 🛟 RESCUED from offline cache: ${fileName}`, "color: #4CAF50; font-weight: bold;");
                    return await cachedResponse.json();
                }

                console.error(`%c[JSON] ❌ FATAL: No network and no offline cache for ${fileName}`, "color: #E91E63; font-weight: bold;");
                throw networkError;
            }
        };

        app.config.globalProperties.$hardResetAllCaches = async function () {
            console.log("%c[Cache] 🚨 INITIATING FULL HARD RESET...", "color: #F44336; font-weight: bold;");

            // Clear RAM
            if (typeof memoryBlobCache !== 'undefined') {
                memoryBlobCache.forEach(blobUrl => URL.revokeObjectURL(blobUrl));
                memoryBlobCache.clear();
                failedMediaUrls.clear();
            }

            // Clear ALL Disk Caches (JSON + Media)
            try {
                await caches.delete(JSON_CACHE_KEY);
                await caches.delete(MEDIA_CACHE_KEY);
                console.log("%c[Cache] 💥 ALL CACHE DELETED.", "color: #F44336; font-weight: bold;");
            } catch (e) {
                console.error("[Cache] Failed to hard reset caches", e);
            }
        };
    }
};
